import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AuthService from 'services/auth.service';
import ModernModal from 'components/ModernModal';
import styles from './TransactionHistory.module.css';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const getColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return '#' + ('00000' + ((hash & 0x00ffffff) >>> 0).toString(16)).slice(-6);
};

const fmtCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(parseFloat(v) || 0);

const fmtDate = (ds) => {
  const d = new Date(ds);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${m[d.getMonth()]} ${String(d.getDate()).padStart(2,'0')}, ${d.getFullYear()}`;
};

const fmtDateTime = (ds) => {
  const d = new Date(ds);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const pad = (n) => String(n).padStart(2,'0');
  return `${m[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

const StatusChip = ({ paid }) => (
  <span className={paid ? styles.chipSuccess : styles.chipPending}>
    <span className={styles.chipDot} style={{ background: paid ? 'var(--success)' : 'var(--warning)' }} />
    {paid ? 'Successful' : 'Pending'}
  </span>
);

const TxAvatar = React.memo(({ title }) => {
  const bg = useMemo(() => getColor(title), [title]);
  return <div className={styles.avatar} style={{ background: bg }}>{title.charAt(0).toUpperCase()}</div>;
});
TxAvatar.displayName = 'TxAvatar';

const SkeletonRow = () => (
  <tr>
    <td className={styles.td}><div style={{ display:'flex', alignItems:'center', gap:8 }}><div className={styles.skel} style={{ width:32, height:32, borderRadius:10 }} /><div className={styles.skelText} style={{ width:100 }} /></div></td>
    <td className={styles.td}><div className={styles.skelText} style={{ width:60 }} /></td>
    <td className={styles.td}><div className={styles.skelText} style={{ width:70 }} /></td>
    <td className={styles.td}><div className={styles.skelText} style={{ width:50 }} /></td>
    <td className={styles.td}><div className={styles.skelText} style={{ width:90 }} /></td>
  </tr>
);

const ReceiptModal = ({ tx, onClose }) => (
  <ModernModal isOpen={!!tx} onClose={onClose} title="Transaction Receipt" size="sm">
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div><span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Amount</span><div style={{ fontWeight:700, fontSize:'1.2rem' }}>{fmtCurrency(tx?.totalFee)}</div></div>
      <div><span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Date</span><div>{fmtDateTime(tx?.createdAt)}</div></div>
      <div><span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Payment Method</span><div>{tx?.paymentMethod}</div></div>
      <div><span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Description</span><div>{tx?.description}</div></div>
      <div><span style={{ color:'var(--text-muted)', fontSize:'0.75rem' }}>Transaction ID</span><div style={{ fontFamily:'var(--font-mono)', fontSize:'0.7rem' }}>{tx?.transactionId}</div></div>
      <div><StatusChip paid={tx?.status} /></div>
    </div>
  </ModernModal>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

export default function TransactionHistory() {
  const user = AuthService.getCurrentUser();
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    AuthService.getAllTransaction(user.id)
      .then(r => {
        const sorted = (r.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setTxs(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id]);

  const filtered = useMemo(() => {
    let list = txs;
    if (filter === 'successful') list = list.filter(t => t.status);
    if (filter === 'pending') list = list.filter(t => !t.status);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t => t.title?.toLowerCase().includes(q) || t.transactionId?.toLowerCase().includes(q) || t.paymentMethod?.toLowerCase().includes(q));
    }
    return list;
  }, [txs, filter, search]);

  const closeModal = useCallback(() => setSelected(null), []);

  return (
    <div className="content mid-content">
      <div className={styles.welcome}>
        <h2 className={styles.welcomeTitle}>Transactions</h2>
        <p className={styles.welcomeSub}>View and manage your transaction history</p>
      </div>

      <div className={styles.filters}>
        {['all', 'successful', 'pending'].map(f => (
          <button key={f} className={filter === f ? styles.filterBtnActive : styles.filterBtn} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <input className={styles.searchInput} placeholder="Search by ID, title, method..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Desktop table */}
      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Transaction</th>
              <th className={styles.th}>Amount</th>
              <th className={styles.th}>Date</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Transaction ID</th>
            </tr>
          </thead>
          <tbody>
            {loading ? [1,2,3,4,5].map(i => <SkeletonRow key={i} />) : filtered.length === 0 ? (
              <tr><td colSpan={5} className={styles.empty}><div className={styles.emptyIcon}>📭</div><div className={styles.emptyText}>No transactions found</div></td></tr>
            ) : filtered.map(tx => (
              <tr key={tx.id} className={styles.row} onClick={() => setSelected(tx)}>
                <td className={styles.td}><div className={styles.titleCell}><TxAvatar title={tx.title} /><span>{tx.title}</span></div></td>
                <td className={styles.td}>{fmtCurrency(tx.totalFee)}</td>
                <td className={styles.td} style={{ whiteSpace:'nowrap' }}>{fmtDate(tx.createdAt)}</td>
                <td className={styles.td}><StatusChip paid={tx.status} /></td>
                <td className={styles.td}><span style={{ fontFamily:'var(--font-mono)', fontSize:'0.7rem' }}>{tx.transactionId}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className={styles.mobileList}>
        {loading ? [1,2,3].map(i => <div key={i} className={styles.mobileCard}><div className={styles.skelText} style={{ width:'70%' }} /><div className={styles.skelText} style={{ width:'40%' }} /></div>) : filtered.map(tx => (
          <div key={tx.id} className={styles.mobileCard} onClick={() => setSelected(tx)}>
            <div className={styles.mobileRow}><div className={styles.titleCell}><TxAvatar title={tx.title} /><strong>{tx.title}</strong></div><StatusChip paid={tx.status} /></div>
            <div className={styles.mobileRow}><span className={styles.mobileLabel}>Amount</span><span>{fmtCurrency(tx.totalFee)}</span></div>
            <div className={styles.mobileRow}><span className={styles.mobileLabel}>Date</span><span>{fmtDate(tx.createdAt)}</span></div>
            <div className={styles.mobileRow}><span className={styles.mobileLabel}>ID</span><span style={{ fontFamily:'var(--font-mono)', fontSize:'0.65rem' }}>{tx.transactionId}</span></div>
          </div>
        ))}
      </div>

      <ReceiptModal tx={selected} onClose={closeModal} />
    </div>
  );
}